import { forwardRef, useEffect, useId, useRef, useState, type TextareaHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Label, HelperText, ErrorText } from './form-helpers.js';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  autoResize?: boolean;
  showCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, helperText, errorText, autoResize = true, showCount = false, className, id: idProp, value, defaultValue, maxLength, onInput, ...rest },
  ref,
) {
  const generated = useId();
  const id = idProp ?? generated;
  const helperId = helperText ? `${id}-helper` : undefined;
  const errorId = errorText ? `${id}-error` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const [count, setCount] = useState(() => {
    if (typeof value === 'string') return value.length;
    if (typeof defaultValue === 'string') return defaultValue.length;
    return 0;
  });

  useEffect(() => {
    if (!autoResize) return;
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [autoResize, value]);

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && (
        <div className="flex items-baseline justify-between gap-2">
          <Label htmlFor={id}>{label}</Label>
          {showCount && maxLength != null && (
            <span className="text-xs text-text-secondary tabular-nums">
              {count}/{maxLength}
            </span>
          )}
        </div>
      )}
      <textarea
        ref={(node) => {
          innerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        id={id}
        value={value}
        defaultValue={defaultValue}
        maxLength={maxLength}
        aria-invalid={!!errorText || undefined}
        aria-describedby={describedBy}
        onInput={(e) => {
          if (autoResize) {
            const t = e.currentTarget;
            t.style.height = 'auto';
            t.style.height = `${t.scrollHeight}px`;
          }
          if (showCount) setCount(e.currentTarget.value.length);
          onInput?.(e);
        }}
        className={cn(
          'min-h-[120px] w-full resize-y rounded-md bg-bg-surface-2 border border-border-strong px-3.5 py-3 text-base text-text-primary placeholder:text-text-tertiary outline-none',
          'focus:border-accent-base focus:ring-2 focus:ring-accent-base/30',
          errorText && 'border-danger focus:border-danger focus:ring-danger/30',
          className,
        )}
        {...rest}
      />
      {errorText && <ErrorText id={errorId}>{errorText}</ErrorText>}
      {helperText && !errorText && <HelperText>{helperText}</HelperText>}
    </div>
  );
});
